import { DEVICE_TYPES, WEB_APP_VERSION } from "common/constants"
import secureLocalStorage from "react-secure-storage"
import Bowser from "bowser"
import { logger } from "utils/logger"

const signalR = require("@microsoft/signalr")

const app = {
  paths: {
    hubs: {
      services: process.env.REACT_APP_SERVICES_HUB,
    },
  },
}

export function createStudentHubConnection({
  baseUrl,
  accessTokenFactory,
}) {
  let myConnection = null
  // Last session data to be used in case of reconnection
  let lastSession = null
  let pendingLogs = []

  const getAccessToken = () => {
    const token = secureLocalStorage.getItem("smashAuthToken")
    return token || ""
  }

  const getDeviceSignature = () => {
    const smashSignature = secureLocalStorage.getItem("smashSignature")
    if (smashSignature !== null) {
      return smashSignature
    } else {
      const str = "0123456789ABCDEF"
      const uuid = []
      for (let i = 0; i < 8; i++) {
        uuid.push(str[Math.floor(Math.random() * str.length)])
      }
      uuid.push("-")
      for (let i = 0; i < 4; i++) {
        uuid.push(str[Math.floor(Math.random() * str.length)])
      }
      uuid.push("-")
      for (let i = 0; i < 4; i++) {
        uuid.push(str[Math.floor(Math.random() * str.length)])
      }
      uuid.push("-")
      for (let i = 0; i < 8; i++) {
        uuid.push(str[Math.floor(Math.random() * str.length)])
      }
      try {
        secureLocalStorage.setItem("smashSignature", uuid.join(""))
      } catch (error) { }
      return uuid.join("")
    }
  }

  const getClientData = () => {
    const { browser, os, platform } = Bowser.parse(window.navigator.userAgent)
    const deviceSO = `${os.name || ""} ${os.version || ""}`
    const deviceName = `${browser.name || ""} ${browser.version || ""} ${platform.type || ""
      }`

    const clientData = {
      deviceTypeId: DEVICE_TYPES.WEB,
      deviceSignature: getDeviceSignature(),
      deviceName,
      deviceSO,
      deviceDetails: "", // Empty for now, can be used to store any additional info if needed
      appVersion: WEB_APP_VERSION, // React version
    }

    return clientData || null
  }

  const start = async ({ channel, userId }, onMessage) => {
    const currentToken = getAccessToken()
    const groupId = channel.split("-")[1] || -1
    if (!app.paths.hubs.services) {
      console.error(
        "Invalid services hub URL. Set REACT_APP_SERVICES_HUB or provide baseUrl."
      )
      return false
    }
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${app.paths.hubs.services}?groupId=${groupId}`, {
        accessTokenFactory: () => currentToken,
      })
      .configureLogging(
        process.env.REACT_APP_ENVIORNMENT !== "production"
          ? signalR.LogLevel.Information
          : signalR.LogLevel.None
      )
      .withAutomaticReconnect({ nextRetryDelayInMilliseconds: 2000 })
      .build()
    myConnection = connection

    connection.on("Notification", (notification) => {
      try {
        onMessage?.({
          ...notification.payload,
          notificationType: notification.type,
        })
      } catch (e) {
        // ignore consumer errors
      }
    })

    connection.on("IsUserConnected", (caller) => {
      connection
        .invoke("UserConnected", userId, caller)
        .catch((err) => console.log("UserConnected error", err))
    })

    connection.onreconnecting((error) => {
      console.log("Connection lost, reconnecting...", error)
    })

    connection.onreconnected(async (connectionId) => {
      console.log(
        "Connection restored. Processing pending logs...",
        connectionId
      )
      // Process pending logs after reconnection
      if (pendingLogs.length > 0) {
        const logsToProcess = [...pendingLogs]
        pendingLogs = []
        for (const log of logsToProcess) {
          await setSessionActivityLog({
            connection,
            ...log,
          })
        }
      }
    })

    connectionStart({ connection, channel })
    return connection
  }

  const connectionStart = async ({ connection, channel }) => {
    try {
      await connection.start()
      await joinChannel({ connection, channel })
      if (lastSession && lastSession !== null) {
        const { viewId, missionsActivityId, exerciseId, gameSessionContentId } = lastSession
        await setSessionActivityLog({
          connection,
          viewId,
          missionsActivityId,
          exerciseId,
          gameSessionContentId
        })
      }
      await checkIndependentWork({ connection, channel })
      await checkCurrentGame({ connection, channel })

      // Process pending logs
      if (pendingLogs.length > 0) {
        const logsToProcess = [...pendingLogs]
        pendingLogs = []
        for (const log of logsToProcess) {
          await setSessionActivityLog({
            connection,
            ...log,
          })
        }
      }
      return true
    } catch (error) {
      // Check if it's a handshake cancellation error
      const isHandshakeCanceled =
        error?.message?.includes("Handshake was canceled") ||
        error?.message?.includes("handshake")

      if (isHandshakeCanceled) {
        // Clean up the connection
        try {
          await connection.stop()
        } catch (stopError) {
          // Ignore stop errors
        }
      }
      return false
    }
  }

  const stop = async () => {
    try {
      if (!myConnection) return

      // Check if connection is in a state that can be stopped
      const connectionState = myConnection.state

      // If still connecting, wait a bit or just return
      if (connectionState === signalR.HubConnectionState.Connecting) {
        // Wait a short time for connection to finish
        await new Promise((resolve) => setTimeout(resolve, 100))
      }

      myConnection.off("Notification")
      myConnection.onclose((error) => {
        console.log("connection closed", error)
      })

      // Only stop if connection is in a stoppable state
      if (myConnection.state !== signalR.HubConnectionState.Disconnected) {
        await myConnection.stop()
      }

      myConnection = null
      console.info("connection stopped")
    } catch (error) {
      // Silently handle stop errors during connecting state
      const isStopWhileConnecting = error?.message?.includes(
        "still in the connecting state"
      )
      if (!isStopWhileConnecting) {
        console.log("~ stop ~ error:", error)
      }
      return false
    }
  }

  const checkCurrentGame = async ({ connection, channel }) => {
    try {
      await (connection || myConnection)?.invoke(
        "CheckForTeacherSmashGame",
        channel
      )
      return true
    } catch (error) {
      return false
    }
  }

  const checkIndependentWork = async ({ connection, channel }) => {
    try {
      await (connection || myConnection)?.invoke(
        "CheckForIndependentWork",
        channel
      )
      return true
    } catch (error) {
      return false
    }
  }

  const joinChannel = async ({ connection, channel }) => {
    try {
      await (connection || myConnection)?.invoke("JoinChannel", channel)
      return true
    } catch (error) {
      return false
    }
  }

  const setSessionActivityLog = async ({
    connection,
    viewId = 0,
    missionsActivityId = 0,
    exerciseId = 0,
    gameSessionContentId = 0
  }) => {
    const sessionData = {
      ...getClientData(),
      viewId,
      missionsActivityId,
      exerciseId,
      gameSessionContentId
    }
    logger.log("setSession request:", sessionData)
    const conn = connection || myConnection
    if (!conn || conn.state !== signalR.HubConnectionState.Connected) {
      pendingLogs.push({ viewId, missionsActivityId, exerciseId, gameSessionContentId })
      return
    }

    try {
      const response = await conn.invoke("SetSession", sessionData)
      logger.log("setSession response:", response)
      lastSession = sessionData
      // Clear queue if it was sent successfully (optional, since we process it in connectionStart)
    } catch (error) {
      console.error("Error setting session activity log:", error)
      // If it failed due to connection, queue it
      if (error?.message?.includes("Connected")) {
        pendingLogs.push({ viewId, missionsActivityId, exerciseId, gameSessionContentId })
      }
    }
  }

  const oldConnection = () => {
    const groupId = 19357
    const userId = 279664
    let onConnectCallbacks = []
    const hubPath = "https://smasheducation.com/services/ServicesHub"
    const userToken = getAccessToken()
    console.log("🚀 ~ oldConnection ~ userToken:", userToken)

    let connection = null
    const channel = `group-${groupId}-notifications`

    connection = new signalR.HubConnectionBuilder()
      .withUrl(`${hubPath}?groupId=${groupId}`, {
        accessTokenFactory: () => userToken,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: 2000,
      })
      .build()

    connection.on("Notification", function (notification) { })

    connection.on("IsUserConnected", function (caller) {
      connection
        .invoke("UserConnected", userId, caller)
        .then(() => { })
        .catch(function (err) {
          console.log("🚀 ~ connection.on ~ err:", err)
        })
    })

    const connectionStart = () => {
      connection
        .start()
        .then(function () {
          connection
            .invoke("JoinChannel", channel)
            .then(() => {
              // checkCurrentGame();
              // checkIndependentWork();

              onConnectCallbacks.forEach((callback) => {
                callback()
              })

              onConnectCallbacks = []
            })
            .catch(function (err) {
              console.log("🚀 ~ connection.on ~ err:", err)
            })
        })
        .catch(function (error) {
          console.log("🚀 ~ connection.on ~ error:", error)
        })
    }

    connectionStart()

    connection.onclose((error) => {
      setTimeout(() => {
        connectionStart()
      }, 2000)

      if (error) {
        console.error(error)
      }
    })

    document.onvisibilitychange = () => {
      if (
        document.visibilityState === "visible" &&
        connection &&
        connection.state !== signalR.HubConnectionState.Connected
      ) {
        connectionStart()
      }
    }
  }

  return {
    connection: myConnection,
    start,
    stop,
    checkIndependentWork,
    checkCurrentGame,
    joinChannel,
    oldConnection,
    getClientData,
    setSessionActivityLog,
  }
}
